import { createApi } from '@reduxjs/toolkit/query/react';
import { authBaseQuery } from '../../app/authBaseQuery';
import { parseLaravelPaginator } from '../../lib/parseLaravelPaginator';
import { unwrapMaybeWrapped } from '../../lib/unwrapResource';
import {
  normalizeInvestment,
  normalizePayment,
  normalizePayout,
  normalizePayoutSummary,
  normalizeRolloverHistoryEntry,
} from './normalizeInvestment';

/**
 * Real-backend admin Investments surface (v1.12.0) — create, list (global
 * and per-investor), detail, and payments. Gated by manage-investments,
 * a permission distinct from manage-investors (the Investors browsing
 * gate) — an admin could hold one without the other, so this slice and
 * adminInvestorsApi.js are deliberately kept separate rather than merged.
 *
 * Every GET here returns its resource directly (bare paginator for lists,
 * bare object for a single investment/payment) — same convention as
 * adminInvestorsApi.js, parsed with the same shared parseLaravelPaginator,
 * not the KYC/Investment-Plans wrapper parsers.
 *
 * Distinct from the old mock-backed features/investments/investmentsApi.js
 * (still present, unused — targeted made-up endpoints like
 * /admin/investments/{id}/rate and /me/investments that don't exist here).
 */
export const adminInvestmentsApi = createApi({
  reducerPath: 'adminInvestmentsApi',
  baseQuery: authBaseQuery(),
  tagTypes: ['AdminInvestment', 'AdminInvestmentPayment', 'AdminInvestmentPayout', 'AdminInvestmentRollover'],
  endpoints: (builder) => ({
    // POST /admin/investments — JSON body, not multipart (no file upload
    // on this one). custom_roi_percentage/custom_duration_in_days are both
    // optional and independent; only send whichever the admin actually
    // set, since sending e.g. 0 for an unset override would flip
    // is_override: true server-side when the admin meant "use the plan
    // default."
    updateInvestmentDates: builder.mutation({
      query: ({ investmentId, ...body }) => ({
        url: `/admin/investments/${investmentId}/dates`, method: 'PATCH', data: body,
      }),
      transformResponse: (response) => normalizeInvestment(unwrapMaybeWrapped(response)),
      invalidatesTags: ['AdminInvestment', 'AdminInvestmentPayout', 'AdminInvestmentRollover'],
    }),
    createInvestment: builder.mutation({
      query: (body) => ({ url: '/admin/investments', method: 'POST', data: body }),
      transformResponse: (response) => normalizeInvestment(unwrapMaybeWrapped(response)),
      invalidatesTags: (result, error, body) => [
        { type: 'AdminInvestment', id: 'LIST' },
        { type: 'AdminInvestment', id: `USER_${body.user_uuid}` },
      ],
    }),

    // GET /admin/investments?status=&per_page=&page=&has_pending_payout= —
    // every investor. `has_pending_payout` must be sent as 1/0 (or omitted),
    // never the string 'true'/'false' — Laravel's boolean validation 422s on
    // that. Only included in params when explicitly set (true -> 1), so an
    // unset filter is simply absent from the query string rather than sent
    // as 0.
    listAdminInvestments: builder.query({
      query: ({ status, page, per_page, has_pending_payout } = {}) => ({
        url: '/admin/investments',
        method: 'GET',
        params: { status, page, per_page, has_pending_payout: has_pending_payout ? 1 : undefined },
      }),
      transformResponse: (response) => {
        const { items, meta, parseError } = parseLaravelPaginator(response, { context: 'adminInvestmentsApi:list' });
        return { investments: items.map(normalizeInvestment), meta, parseError: !!parseError };
      },
      providesTags: (result) => [
        ...(result?.investments ?? []).map(({ id }) => ({ type: 'AdminInvestment', id })),
        { type: 'AdminInvestment', id: 'LIST' },
      ],
    }),

    // GET /admin/users/{uuid}/investments?status=&per_page=&has_pending_payout=
    // — scoped to one investor, powers the embedded list on Investor Detail.
    listUserInvestments: builder.query({
      query: ({ uuid, status, page, per_page, has_pending_payout } = {}) => ({
        url: `/admin/users/${uuid}/investments`,
        method: 'GET',
        params: { status, page, per_page, has_pending_payout: has_pending_payout ? 1 : undefined },
      }),
      transformResponse: (response) => {
        const { items, meta, parseError } = parseLaravelPaginator(response, { context: 'adminInvestmentsApi:userInvestments' });
        return { investments: items.map(normalizeInvestment), meta, parseError: !!parseError };
      },
      providesTags: (result, error, { uuid }) => [
        ...(result?.investments ?? []).map(({ id }) => ({ type: 'AdminInvestment', id })),
        { type: 'AdminInvestment', id: `USER_${uuid}` },
      ],
    }),

    // GET /admin/investments/{investment}
    getAdminInvestment: builder.query({
      query: (id) => ({ url: `/admin/investments/${id}`, method: 'GET' }),
      transformResponse: (response) => normalizeInvestment(unwrapMaybeWrapped(response)),
      providesTags: (result, error, id) => [{ type: 'AdminInvestment', id }],
    }),

    // GET /admin/investments/{investment}/payments?per_page=
    listInvestmentPayments: builder.query({
      query: ({ investmentId, page, per_page } = {}) => ({
        url: `/admin/investments/${investmentId}/payments`,
        method: 'GET',
        params: { page, per_page },
      }),
      transformResponse: (response) => {
        const { items, meta, parseError } = parseLaravelPaginator(response, { context: 'adminInvestmentsApi:payments' });
        return { payments: items.map(normalizePayment), meta, parseError: !!parseError };
      },
      providesTags: (result, error, { investmentId }) => [{ type: 'AdminInvestmentPayment', id: investmentId }],
    }),

    // POST /admin/investments/{investment}/payments — 409 if the
    // investment isn't pending_funding anymore; the UI also hides/disables
    // Record Payment once status flips to active, so that's a backstop,
    // not the primary guard. A payment that fully funds the investment
    // flips its status to active as a side effect — invalidating the
    // investment's own tag (not just the payments list) is what picks
    // that up.
    recordPayment: builder.mutation({
      query: ({ investmentId, ...body }) => ({
        url: `/admin/investments/${investmentId}/payments`,
        method: 'POST',
        data: body,
      }),
      transformResponse: (response) => normalizePayment(unwrapMaybeWrapped(response)),
      invalidatesTags: (result, error, { investmentId }) => [
        { type: 'AdminInvestment', id: investmentId },
        { type: 'AdminInvestment', id: 'LIST' },
        { type: 'AdminInvestmentPayment', id: investmentId },
      ],
    }),

    // GET /admin/investments/{investment}/payouts?per_page= — v1.13.0,
    // shape extended in v1.16.0. Payouts are a distinct concept/table from
    // payments (see normalizePayout's comment) — money going back out to
    // the investor, not funding coming in. Server orders by paid_at
    // descending; no client-side sort needed.
    //
    // v1.16.0 adds a top-level `summary` (PayoutSummary) alongside the
    // paginated `data` — the authoritative ledger snapshot for this
    // investment's payout schedule. Every summary figure shown in the UI
    // must come from this object, never be recomputed by summing payout
    // rows client-side.
    listInvestmentPayouts: builder.query({
      query: ({ investmentId, page, per_page } = {}) => ({
        url: `/admin/investments/${investmentId}/payouts`,
        method: 'GET',
        params: { page, per_page },
      }),
      transformResponse: (response) => {
        const { items, meta, parseError } = parseLaravelPaginator(response, { context: 'adminInvestmentsApi:payouts' });
        return {
          payouts: items.map(normalizePayout),
          summary: normalizePayoutSummary(response?.summary),
          meta,
          parseError: !!parseError,
        };
      },
      providesTags: (result, error, { investmentId }) => [{ type: 'AdminInvestmentPayout', id: investmentId }],
    }),

    // POST /admin/investments/{investment}/payouts — v1.13.0, breaking body
    // shape change in v1.16.0: { type: 'principal'|'interest', amount,
    // paid_at, note } instead of a combined { principal, interest } pair.
    // Returns 200, not 201, unlike payment creation — doesn't matter to
    // this HTTP layer (authAxios/authBaseQuery treat any 2xx as success,
    // nothing here branches on the exact status code), called out only
    // because the docs flag it as a deliberate quirk worth not assuming
    // away elsewhere.
    //
    // v1.16.0: unlike before, a payout CAN now change the investment's own
    // fields (is_paid_out, principal_is_paid, payout_summary all live on
    // the investment and update as payouts are recorded) — invalidating
    // the investment's own tag (and the list tag, since has_pending_payout
    // filtering/card indicators read from the same fields) is required so
    // those views refresh, not just the payout list.
    recordPayout: builder.mutation({
      query: ({ investmentId, ...body }) => ({
        url: `/admin/investments/${investmentId}/payouts`,
        method: 'POST',
        data: body,
      }),
      transformResponse: (response) => normalizePayout(unwrapMaybeWrapped(response)),
      // v1.17.0: a payout that pays off the current term's interest can
      // flip a scheduled rollover to executed, and a principal payout
      // auto-cancels any scheduled rollover server-side — invalidate the
      // rollover tag too so the status card/history refresh without a
      // manual reload.
      invalidatesTags: (result, error, { investmentId }) => [
        { type: 'AdminInvestmentPayout', id: investmentId },
        { type: 'AdminInvestment', id: investmentId },
        { type: 'AdminInvestment', id: 'LIST' },
        { type: 'AdminInvestmentRollover', id: investmentId },
      ],
    }),

    // POST /admin/investments/{investment}/close — v1.13.0. Ends the
    // investment's lifecycle; the docs don't spell out which statuses are
    // closeable, so this doesn't gate client-side beyond the UI's own
    // obvious-nonsense check (see InvestmentDetail.jsx) — a 409 here means
    // the backend rejected the current status, surfaced as-is rather than
    // guessed at.
    closeInvestment: builder.mutation({
      query: ({ investmentId, reason }) => ({
        url: `/admin/investments/${investmentId}/close`,
        method: 'POST',
        data: reason ? { reason } : {},
      }),
      transformResponse: (response) => normalizeInvestment(unwrapMaybeWrapped(response)),
      // v1.17.0: closing an investment auto-cancels any scheduled rollover
      // server-side — invalidate the rollover tag alongside the investment.
      invalidatesTags: (result, error, { investmentId }) => [
        { type: 'AdminInvestment', id: investmentId },
        { type: 'AdminInvestment', id: 'LIST' },
        { type: 'AdminInvestmentRollover', id: investmentId },
      ],
    }),

    // POST /admin/investments/{investment}/rollover — v1.17.0. Body: { note }
    // (optional). Executes immediately if the current term's interest is
    // already fully paid at request time, otherwise schedules the rollover
    // to auto-execute on the final interest payout or the nightly sweep —
    // the response's `rollover` block (state/scheduled_rollover) is what
    // tells the UI which of the two happened; this endpoint never signals
    // that distinction any other way. 422 means the backend judged the
    // investment ineligible right now — its message is written to explain
    // why and must be surfaced to the admin as-is, not replaced with a
    // generic error.
    requestRollover: builder.mutation({
      query: ({ investmentId, note }) => ({
        url: `/admin/investments/${investmentId}/rollover`,
        method: 'POST',
        data: note ? { note } : {},
      }),
      transformResponse: (response) => normalizeInvestment(unwrapMaybeWrapped(response)),
      invalidatesTags: (result, error, { investmentId }) => [
        { type: 'AdminInvestment', id: investmentId },
        { type: 'AdminInvestment', id: 'LIST' },
        { type: 'AdminInvestmentRollover', id: investmentId },
      ],
    }),

    // DELETE /admin/investments/{investment}/rollover — v1.17.0. Cancels a
    // scheduled (not-yet-executed) rollover. Body: { reason }. Only
    // meaningful when `rollover.scheduled_rollover` is non-null; the UI
    // gates the action on that, this call doesn't re-check it client-side.
    cancelRollover: builder.mutation({
      query: ({ investmentId, reason }) => ({
        url: `/admin/investments/${investmentId}/rollover`,
        method: 'DELETE',
        data: { reason },
      }),
      transformResponse: (response) => normalizeInvestment(unwrapMaybeWrapped(response)),
      invalidatesTags: (result, error, { investmentId }) => [
        { type: 'AdminInvestment', id: investmentId },
        { type: 'AdminInvestment', id: 'LIST' },
        { type: 'AdminInvestmentRollover', id: investmentId },
      ],
    }),

    // GET /admin/investments/{investment}/rollovers?per_page= — v1.17.0.
    // Rollover history, newest-sequence-first (server-ordered, no
    // client-side re-sort). Bare Laravel paginator, same convention as
    // payments/payouts above.
    listInvestmentRollovers: builder.query({
      query: ({ investmentId, page, per_page } = {}) => ({
        url: `/admin/investments/${investmentId}/rollovers`,
        method: 'GET',
        params: { page, per_page },
      }),
      transformResponse: (response) => {
        const { items, meta, parseError } = parseLaravelPaginator(response, { context: 'adminInvestmentsApi:rollovers' });
        return { rollovers: items.map(normalizeRolloverHistoryEntry), meta, parseError: !!parseError };
      },
      providesTags: (result, error, { investmentId }) => [{ type: 'AdminInvestmentRollover', id: investmentId }],
    }),
  }),
});

export const {
  useUpdateInvestmentDatesMutation,
  useCreateInvestmentMutation,
  useListAdminInvestmentsQuery,
  useListUserInvestmentsQuery,
  useGetAdminInvestmentQuery,
  useListInvestmentPaymentsQuery,
  useRecordPaymentMutation,
  useListInvestmentPayoutsQuery,
  useRecordPayoutMutation,
  useCloseInvestmentMutation,
  useRequestRolloverMutation,
  useCancelRolloverMutation,
  useListInvestmentRolloversQuery,
} = adminInvestmentsApi;
